/**
 * Client-side table sort (spec-web-ui §2: "rūšiavimas klientinėje pusėje",
 * matching the ModulinaPanele3 dialog) — pure, unit-tested in
 * test/ui/sort.test.ts. DOM-free (compiles under both tsconfigs).
 */

export type SortDir = 1 | -1;

/** Lithuanian-aware, case-insensitive string collation (ą/č/ė sort where an
 * employee expects them, not after "z"). `sensitivity: "base"` also folds
 * case without a manual toLowerCase pass. */
const collator = new Intl.Collator("lt", { sensitivity: "base" });

/** Cell access by dynamic key — the wire row interfaces carry no index
 * signature (deliberately exact), so the lookup goes through unknown. */
function cell(row: object, key: string): unknown {
  return (row as Record<string, unknown>)[key];
}

/**
 * Returns a SORTED COPY (never mutates — Lit change detection depends on
 * identity). `numericKeys` members compare as numbers with null/undefined
 * as 0 (empty cells sink on ascending); everything else collates as
 * Lithuanian text with null/undefined as "". Array.prototype.sort is
 * stable per spec, so equal cells keep their server order.
 */
export function sortRows<T extends object>(
  rows: readonly T[],
  key: string,
  dir: SortDir,
  numericKeys: ReadonlySet<string>,
): T[] {
  const sorted = [...rows];
  if (numericKeys.has(key)) {
    sorted.sort((a, b) => (toNumber(cell(a, key)) - toNumber(cell(b, key))) * dir);
  } else {
    sorted.sort((a, b) => collator.compare(toText(cell(a, key)), toText(cell(b, key))) * dir);
  }
  return sorted;
}

/** One header click: same key flips direction, a new key starts ascending. */
export function nextSort(
  current: { key: string | null; dir: SortDir },
  clickedKey: string,
): { key: string; dir: SortDir } {
  if (current.key === clickedKey) {
    return { key: clickedKey, dir: current.dir === 1 ? -1 : 1 };
  }
  return { key: clickedKey, dir: 1 };
}

function toNumber(value: unknown): number {
  return typeof value === "number" && !Number.isNaN(value) ? value : 0;
}

function toText(value: unknown): string {
  return value === null || value === undefined ? "" : String(value);
}
